import { getRuntimeConfiguration } from '@/lib/config';
import { ValidationErrorResponse } from '@/lib/types';

export type FetchResult<T> = {
  data: T | null;
  hasError: boolean;
  error: string | null | ValidationErrorResponse;
};

type FetchOptions = {
  method?: 'GET' | 'PUT' | 'POST' | 'DELETE';
  body?: object;
  params?: Record<string, string>;
  jwt?: string | null;
  channelId?: string;
  url?: string; // Defaults to Configurator API
};

const getSearchParams = (params: FetchOptions['params']) => {
  if (!params) {
    return '';
  }
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, val]) => {
    if (val) {
      search.set(key, val);
    }
  });
  return `?${search.toString()}`;
};

export const fetchData = async <T>(path: string, options?: FetchOptions): Promise<FetchResult<T>> => {
  const headers = new Headers();
  headers.set('accept', 'application/json');
  headers.set('Content-Type', 'application/json');
  if (getRuntimeConfiguration('CONTENT_API_KEY')) {
    headers.set('api-key', getRuntimeConfiguration('CONTENT_API_KEY'));
  }
  if (options?.jwt) {
    headers.set('Authorization', `Bearer ${options?.jwt}`);
  }
  if (options?.channelId) {
    headers.set('X-Channel-Id', options.channelId);
  }
  try {
    const { method, body, params, url } = options || {};
    const requestUrl = url || getRuntimeConfiguration('CONFIGURATOR_API_URL');
    const searchParams = getSearchParams(params);
    const response = await fetch(`${requestUrl}${path}${searchParams}`, {
      method,
      headers,
      cache: 'no-cache',
      body: body ? JSON.stringify(body) : undefined,
    });
    const data = await response.json();
    if (response.ok) {
      return {
        data,
        hasError: false,
        error: null,
      };
    }
    return {
      data: null,
      hasError: true,
      error: data?.message || data || null,
    };
  } catch (e) {
    return {
      data: null,
      hasError: true,
      error: e instanceof Error ? e.message : (e as string),
    };
  }
};
